import { useState, useEffect, useMemo } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Fade, TextField, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

function TrialBalanceReport() {
  useSessionValidation();

  const { companyId, branchId, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];
  const nowD = new Date();
  const firstOfMonth = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-01`;

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { setMounted(true); }, []);

  const fetchTrialBalance = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const response = await fetchData(
        'ReportApi',
        `/Trialbalance/${companyId}/${branchId || 0}/${fromDate}/${toDate}`,
        token
      );
      const rows = response?.response_data || response || [];
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching trial balance:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Group raw data by headName — all items have both AccountName and headName
  const groupedRaw = useMemo(() => {
    const groupMap = new Map();

    data.forEach(item => {
      const headName = item.headName || 'Uncategorized';
      
      if (!groupMap.has(headName)) {
        groupMap.set(headName, { headName, rows: [] });
      }
      
      groupMap.get(headName).rows.push(item);
    });

    return Array.from(groupMap.values());
  }, [data]);

  // Apply search filter — keep a group if any of its rows match
  const groupedData = useMemo(() => {
    if (!searchTerm.trim()) return groupedRaw;
    const q = searchTerm.toLowerCase();
    return groupedRaw
      .map(group => ({
        ...group,
        rows: group.rows.filter(r => r.AccountName.toLowerCase().includes(q)),
      }))
      .filter(group => group.rows.length > 0);
  }, [groupedRaw, searchTerm]);

  // Flat filtered list (for Excel export)
  const filteredData = useMemo(() => {
    const flat = [];
    groupedData.forEach(group => {
      if (group.headName) flat.push({ headName: group.headName });
      group.rows.forEach(r => flat.push(r));
    });
    return flat;
  }, [groupedData]);

  // Totals across all account rows
  const { totalDebit, totalCredit } = useMemo(() =>
    groupedData.reduce((acc, group) => {
      group.rows.forEach(item => {
        acc.totalDebit += Math.abs(parseFloat(item.debit || 0));
        acc.totalCredit += Math.abs(parseFloat(item.credit || 0));
      });
      return acc;
    }, { totalDebit: 0, totalCredit: 0 }),
    [groupedData]
  );

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const COLS = 3;

    const aoa = [
      [companyName || 'Trial Balance', '', ''],
      ['Trial Balance', '', ''],
      [`Date Range : ${fromDate}  To : ${toDate}`, '', ''],
      ['', '', ''],
      ['Account Name', 'Debit', 'Credit'],
    ];

    filteredData.forEach(item => {
      if (!item.AccountName && item.headName) {
        aoa.push([item.headName, '', '']);
      } else if (item.AccountName) {
        aoa.push([
          `  ${item.AccountName}`,
          Math.abs(parseFloat(item.debit || 0)),
          Math.abs(parseFloat(item.credit || 0)),
        ]);
      }
    });

    aoa.push(['Total', totalDebit, totalCredit]);

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 40 }, { wch: 18 }, { wch: 18 }];
    ws['!rows'] = [{ hpt: 22 }, { hpt: 18 }, { hpt: 16 }, { hpt: 6 }, { hpt: 20 }];
    ws['!merges'] = [0, 1, 2, 3].map(r => ({ s: { r, c: 0 }, e: { r, c: COLS - 1 } }));

    const infoS = (sz, bold, color = '5c3d1e') => ({
      font: { bold, sz, color: { rgb: color } },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
    });
    ws['A1'].s = infoS(16, true);
    ws['A2'].s = infoS(13, true, '2d3748');
    ws['A3'].s = infoS(11, false, '444444');

    const hdrS = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top:    { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left:   { style: 'thin',   color: { rgb: '3b2410' } },
        right:  { style: 'thin',   color: { rgb: '3b2410' } },
      },
    };
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 4, c });
      if (ws[addr]) ws[addr].s = hdrS;
    }

    let excelRow = 5;
    filteredData.forEach(item => {
      if (!item.AccountName && item.headName) {
        for (let c = 0; c < COLS; c++) {
          const addr = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[addr]) ws[addr] = { t: 's', v: '' };
          ws[addr].s = {
            font: { bold: true, sz: 11, color: { rgb: '5c3d1e' } },
            fill: { patternType: 'solid', fgColor: { rgb: 'F5EDE0' } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: {
              top:    { style: 'thin', color: { rgb: 'C9A84C' } },
              bottom: { style: 'thin', color: { rgb: 'C9A84C' } },
              left:   { style: 'thin', color: { rgb: 'C9A84C' } },
              right:  { style: 'thin', color: { rgb: 'C9A84C' } },
            },
          };
        }
      } else if (item.AccountName) {
        for (let c = 0; c < COLS; c++) {
          const addr = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[addr]) ws[addr] = { t: 's', v: '' };
          ws[addr].s = {
            font: { sz: 10, color: { rgb: '2d3748' } },
            fill: { patternType: 'solid', fgColor: { rgb: 'FFFFFF' } },
            alignment: { horizontal: c > 0 ? 'right' : 'left', vertical: 'center' },
            border: {
              top:    { style: 'thin', color: { rgb: 'E0E0E0' } },
              bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
              left:   { style: 'thin', color: { rgb: 'E0E0E0' } },
              right:  { style: 'thin', color: { rgb: 'E0E0E0' } },
            },
          };
        }
      }
      excelRow++;
    });

    // Total row
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: excelRow, c });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = {
        font: { bold: true, sz: 12, color: { rgb: 'BF360C' } },
        fill: { patternType: 'solid', fgColor: { rgb: 'FFF3E0' } },
        alignment: { horizontal: c > 0 ? 'right' : 'left', vertical: 'center' },
        border: {
          top:    { style: 'medium', color: { rgb: 'E65100' } },
          bottom: { style: 'medium', color: { rgb: 'E65100' } },
          left:   { style: 'thin',   color: { rgb: 'E65100' } },
          right:  { style: 'thin',   color: { rgb: 'E65100' } },
        },
      };
    }

    XLSX.utils.book_append_sheet(wb, ws, 'TrialBalance');
    XLSX.writeFile(wb, `TrialBalance_${fromDate}_${toDate}.xlsx`, { bookSST: false, cellStyles: true });
  };

  // ── colour tokens (matching screenshot) ──────────────────────────────────
  const C = {
    headerBg:   '#4a3520',   // dark brown table header
    headerText: '#ffffff',
    groupBg:    '#dde3ea',   // light blue-grey for head rows
    groupText:  '#1a202c',
    rowEven:    '#ffffff',
    rowOdd:     '#f7f8fa',
    rowText:    '#1a202c',
    divider:    '#c8cdd5',   // vertical line between columns
    totalBg:    '#4a3520',
    totalText:  '#ffffff',
    border:     '#c8cdd5',
    accent:     '#c9a84c',
  };

  const thCell = (align = 'left', width) => ({
    background: C.headerBg,
    color: C.headerText,
    fontWeight: '700',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    padding: '7px 12px',
    textAlign: align,
    whiteSpace: 'nowrap',
    borderRight: `1px solid #6b4f30`,
    ...(width ? { width } : {}),
  });

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#f4f6f8', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Trial Balance..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.10)', padding: '12px',
          fontFamily: 'Arial, sans-serif', overflow: 'hidden',
        }}>

          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: `2px solid ${C.accent}` }}>
            <h4 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: C.headerBg, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ⚖️ Trial Balance
            </h4>
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>From</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ border: `1px solid ${C.accent}`, borderRadius: '5px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: `1px solid ${C.accent}`, borderRadius: '5px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <Button variant="contained" onClick={fetchTrialBalance}
              style={{ background: '#2e7d32', color: 'white', padding: '4px 16px', borderRadius: '5px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: 'none' }}>
              Refresh
            </Button>

            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{ background: filteredData.length === 0 ? '#ccc' : '#1565c0', color: 'white', padding: '4px 16px', borderRadius: '5px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: 'none' }}>
              📥 Excel
            </Button>

            <div style={{ flex: 1 }} />

            <TextField size="small" placeholder="Search account..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: C.accent, fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#fdf6ec', fontSize: '12px', height: '29px', '&:hover fieldset': { borderColor: C.accent }, '&.Mui-focused fieldset': { borderColor: C.accent } } }} />
          </div>

          {/* Table */}
          <style>{`
            .tb-scroll::-webkit-scrollbar { height: 8px; width: 6px; }
            .tb-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
            .tb-scroll::-webkit-scrollbar-thumb { background: ${C.accent}; border-radius: 3px; }
            .tb-scroll { scrollbar-width: thin; scrollbar-color: ${C.accent} #f1f1f1; }
            .tb-acc-row:hover td { background: #eef2ff !important; }
          `}</style>

          <div className="tb-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 230px)', border: `1px solid ${C.border}`, borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '500px' }}>
              <colgroup>
                <col style={{ width: '60%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>

              {/* Sticky header */}
              <thead>
                <tr>
                  <th style={thCell('left')}>Account Name</th>
                  <th style={thCell('right')}>Debit</th>
                  <th style={{ ...thCell('right'), borderRight: 'none' }}>Credit</th>
                </tr>
              </thead>

              <tbody>
                {groupedData.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', padding: '32px', color: '#999', fontSize: '13px' }}>
                      {loading ? 'Loading...' : 'No records found. Select dates and click Refresh.'}
                    </td>
                  </tr>
                ) : (
                  groupedData.flatMap((group, gi) => [
                    // ── Head row ──────────────────────────────────────────
                    <tr key={`h-${gi}`}>
                      <td colSpan={3} style={{
                        background: C.groupBg,
                        color: C.groupText,
                        fontWeight: '700',
                        fontSize: '12px',
                        padding: '6px 12px',
                        borderTop: `1px solid ${C.border}`,
                        borderBottom: `1px solid ${C.border}`,
                      }}>
                        {group.headName}
                      </td>
                    </tr>,

                    // ── Account rows ──────────────────────────────────────
                    ...group.rows.map((item, ri) => (
                      <tr key={`r-${gi}-${ri}`} className="tb-acc-row">
                        <td style={{
                          background: ri % 2 === 0 ? C.rowEven : C.rowOdd,
                          color: C.rowText,
                          fontSize: '12px',
                          padding: '5px 12px 5px 22px',
                          borderBottom: `1px solid ${C.border}`,
                        }}>
                          {item.AccountName}
                        </td>
                        <td style={{
                          background: ri % 2 === 0 ? C.rowEven : C.rowOdd,
                          color: C.rowText,
                          fontSize: '12px',
                          padding: '5px 12px',
                          textAlign: 'right',
                          borderBottom: `1px solid ${C.border}`,
                          borderLeft: `1px solid ${C.divider}`,
                        }}>
                          {fmt(Math.abs(parseFloat(item.debit || 0)))}
                        </td>
                        <td style={{
                          background: ri % 2 === 0 ? C.rowEven : C.rowOdd,
                          color: C.rowText,
                          fontSize: '12px',
                          padding: '5px 12px',
                          textAlign: 'right',
                          borderBottom: `1px solid ${C.border}`,
                          borderLeft: `1px solid ${C.divider}`,
                        }}>
                          {fmt(Math.abs(parseFloat(item.credit || 0)))}
                        </td>
                      </tr>
                    )),
                  ])
                )}

                {/* Total row */}
                {groupedData.length > 0 && (
                  <tr>
                    <td style={{ background: C.totalBg, color: C.totalText, fontWeight: '700', fontSize: '12px', padding: '7px 12px', borderTop: `2px solid ${C.accent}`, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Total
                    </td>
                    <td style={{ background: C.totalBg, color: C.totalText, fontWeight: '700', fontSize: '12px', padding: '7px 12px', textAlign: 'right', borderTop: `2px solid ${C.accent}`, borderLeft: `1px solid #6b4f30` }}>
                      {fmt(totalDebit)}
                    </td>
                    <td style={{ background: C.totalBg, color: C.totalText, fontWeight: '700', fontSize: '12px', padding: '7px 12px', textAlign: 'right', borderTop: `2px solid ${C.accent}`, borderLeft: `1px solid #6b4f30` }}>
                      {fmt(totalCredit)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </Fade>
  );
}

export default TrialBalanceReport;
